import React, { useEffect, useState, Component } from 'react';
import { FileText, Plus, Edit2, Trash2, Link as LinkIcon, DollarSign, Check, X, Copy, ExternalLink } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
interface Invoice {
  id: string;
  rentalId: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  status: 'Draft' | 'Sent' | 'Partial Payment' | 'Paid in Full';
  lineItems: LineItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  created: string;
  paymentLink?: string;
}
interface LineItem {
  id: string;
  description: string;
  quantity: number;
  price: number;
  total: number;
}
interface Rental {
  id: string;
  name: string;
  email: string;
  eventName: string;
  total: number;
  selections: any;
  proposedEventDate: string;
}
export function InvoiceManager({
  rentals
}: {
  rentals: any[];
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  useEffect(() => {
    loadInvoices();
  }, []);
  const loadInvoices = () => {
    const stored = localStorage.getItem('invoices');
    if (stored) {
      try {
        setInvoices(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading invoices:', error);
        setInvoices([]);
      }
    }
  };
  const saveInvoices = (updatedInvoices: Invoice[]) => {
    localStorage.setItem('invoices', JSON.stringify(updatedInvoices));
    setInvoices(updatedInvoices);
  };
  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  };
  const createInvoiceFromRental = (rental: Rental) => {
    const lineItems: LineItem[] = [];
    if (rental.selections.fullGen3Ring > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Full Gen 3 Ring',
        quantity: rental.selections.fullGen3Ring,
        price: 900,
        total: rental.selections.fullGen3Ring * 900
      });
    }
    if (rental.selections.gen3Body > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Body',
        quantity: rental.selections.gen3Body,
        price: 700,
        total: rental.selections.gen3Body * 700
      });
    }
    if (rental.selections.gen3Head > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Head',
        quantity: rental.selections.gen3Head,
        price: 425,
        total: rental.selections.gen3Head * 425
      });
    }
    if (rental.selections.gen3JudgesOnly > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Judges Only',
        quantity: rental.selections.gen3JudgesOnly,
        price: 350,
        total: rental.selections.gen3JudgesOnly * 350
      });
    }
    if (rental.selections.requireTech) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'On-site Technician',
        quantity: 1,
        price: 500,
        total: 500
      });
    }
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    const newInvoice: Invoice = {
      id: Date.now().toString(),
      rentalId: rental.id,
      invoiceNumber: generateInvoiceNumber(),
      customerName: rental.name,
      customerEmail: rental.email,
      status: 'Draft',
      lineItems,
      subtotal,
      discount: 0,
      tax: 0,
      total: subtotal,
      amountPaid: 0,
      balance: subtotal,
      created: new Date().toISOString()
    };
    const updated = [...invoices, newInvoice];
    saveInvoices(updated);
    setEditingInvoice(newInvoice);
    setShowCreateModal(false);
  };
  const updateInvoice = (invoice: Invoice) => {
    const subtotal = invoice.lineItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - invoice.discount + invoice.tax;
    const balance = total - invoice.amountPaid;
    const updated = {
      ...invoice,
      subtotal,
      total,
      balance,
      status: balance <= 0 ? 'Paid in Full' as const : invoice.amountPaid > 0 ? 'Partial Payment' as const : invoice.status
    };
    const updatedInvoices = invoices.map(inv => inv.id === updated.id ? updated : inv);
    saveInvoices(updatedInvoices);
    setEditingInvoice(null);
  };
  const deleteInvoice = (invoiceId: string) => {
    if (confirm('Are you sure you want to delete this invoice?')) {
      const updated = invoices.filter(inv => inv.id !== invoiceId);
      saveInvoices(updated);
    }
  };
  const generatePaymentLink = (invoice: Invoice) => {
    // Encode the entire invoice data in the URL as base64
    const invoiceData = JSON.stringify({
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      customerName: invoice.customerName,
      customerEmail: invoice.customerEmail,
      lineItems: invoice.lineItems,
      subtotal: invoice.subtotal,
      discount: invoice.discount,
      tax: invoice.tax,
      total: invoice.total,
      amountPaid: invoice.amountPaid,
      balance: invoice.balance,
      status: invoice.status
    });
    const encodedData = btoa(encodeURIComponent(invoiceData));
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/pay/${invoice.id}?data=${encodedData}`;
    console.log('Generated payment link:', link);
    console.log('Invoice data being encoded:', invoiceData);
    // Update invoice with payment link and save to localStorage
    const currentInvoices = [...invoices];
    const invoiceIndex = currentInvoices.findIndex(inv => inv.id === invoice.id);
    if (invoiceIndex !== -1) {
      currentInvoices[invoiceIndex] = {
        ...currentInvoices[invoiceIndex],
        paymentLink: link,
        status: 'Sent' as const
      };
      localStorage.setItem('invoices', JSON.stringify(currentInvoices));
      console.log('Updated invoices in localStorage:', currentInvoices);
      setInvoices(currentInvoices);
    }
    return link;
  };
  const copyPaymentLink = (link: string, invoiceId: string) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(invoiceId);
    setTimeout(() => setCopiedLink(null), 2000);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'Sent':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Partial Payment':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Paid in Full':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  const rentalsWithInvoices = rentals.map(rental => ({
    ...rental,
    hasInvoice: invoices.some(inv => inv.rentalId === rental.id),
    invoice: invoices.find(inv => inv.rentalId === rental.id)
  }));
  return <div className="space-y-6" data-id="element-179">
      {/* Invoices List */}
      <div data-id="element-180">
        <div className="flex items-center justify-between mb-4" data-id="element-181">
          <h3 className="text-lg font-semibold text-slate-800" data-id="element-182">Invoices</h3>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-183">
            <Plus size={16} data-id="element-184" />
            Create Invoice
          </button>
        </div>

        {invoices.length === 0 ? <div className="bg-slate-50 rounded-lg p-8 text-center" data-id="element-185">
            <FileText size={48} className="text-slate-300 mx-auto mb-3" data-id="element-186" />
            <p className="text-slate-600" data-id="element-187">No invoices created yet</p>
            <p className="text-slate-500 text-sm mt-1" data-id="element-188">
              Create an invoice from a rental request
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-189">
            {invoices.map(invoice => <div key={invoice.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-190">
                <div className="flex items-start justify-between mb-3" data-id="element-191">
                  <div data-id="element-192">
                    <p className="font-mono text-sm font-semibold text-slate-800" data-id="element-193">
                      {invoice.invoiceNumber}
                    </p>
                    <p className="text-sm text-slate-600 mt-1" data-id="element-194">
                      {invoice.customerName}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(invoice.status)}`} data-id="element-195">
                    {invoice.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4" data-id="element-196">
                  <div className="flex justify-between text-sm" data-id="element-197">
                    <span className="text-slate-600" data-id="element-198">Total:</span>
                    <span className="font-semibold text-slate-800" data-id="element-199">
                      ${invoice.total.toFixed(2)}
                    </span>
                  </div>
                  {invoice.amountPaid > 0 && <>
                      <div className="flex justify-between text-sm" data-id="element-200">
                        <span className="text-slate-600" data-id="element-201">Paid:</span>
                        <span className="text-green-600 font-medium" data-id="element-202">
                          ${invoice.amountPaid.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-203">
                        <span className="text-slate-600" data-id="element-204">Balance:</span>
                        <span className="font-semibold text-red-600" data-id="element-205">
                          ${invoice.balance.toFixed(2)}
                        </span>
                      </div>
                    </>}
                </div>

                {/* Payment Link Display (Debug) */}
                {invoice.paymentLink && <div className="mb-3 p-2 bg-slate-50 rounded border border-slate-200" data-id="element-206">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-207">
                      Customer Link:
                    </p>
                    <a href={invoice.paymentLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 underline break-all flex items-center gap-1" data-id="element-208">
                      <ExternalLink size={10} data-id="element-209" />
                      View Invoice
                    </a>
                  </div>}

                <div className="flex gap-2" data-id="element-210">
                  <button onClick={() => setEditingInvoice(invoice)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors text-sm" data-id="element-211">
                    <Edit2 size={14} data-id="element-212" />
                    Edit
                  </button>
                  {invoice.paymentLink ? <button onClick={() => copyPaymentLink(invoice.paymentLink!, invoice.id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm" data-id="element-213">
                      {copiedLink === invoice.id ? <>
                          <Check size={14} data-id="element-214" />
                          Copied
                        </> : <>
                          <Copy size={14} data-id="element-215" />
                          Copy Link
                        </>}
                    </button> : <button onClick={() => {
              const link = generatePaymentLink(invoice);
              copyPaymentLink(link, invoice.id);
            }} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm" data-id="element-216">
                      <LinkIcon size={14} data-id="element-217" />
                      Generate Link
                    </button>}
                </div>
              </div>)}
          </div>}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4" data-id="element-218">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" data-id="element-219">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between" data-id="element-220">
              <h3 className="text-xl font-bold text-slate-800" data-id="element-221">
                Create Invoice from Rental
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-222">
                <X size={20} data-id="element-223" />
              </button>
            </div>
            <div className="p-6" data-id="element-224">
              <p className="text-slate-600 mb-4" data-id="element-225">
                Select a rental to create an invoice:
              </p>
              <div className="space-y-2" data-id="element-226">
                {rentalsWithInvoices.map(rental => <button key={rental.id} onClick={() => createInvoiceFromRental(rental)} disabled={rental.hasInvoice} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${rental.hasInvoice ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-50' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'}`} data-id="element-227">
                    <div className="flex items-center justify-between" data-id="element-228">
                      <div data-id="element-229">
                        <p className="font-semibold text-slate-800" data-id="element-230">
                          {rental.name}
                        </p>
                        <p className="text-sm text-slate-600" data-id="element-231">
                          {rental.eventName}
                        </p>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-232">
                          {rental.proposedEventDate ? new Date(rental.proposedEventDate).toLocaleDateString() : 'No date set'}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-233">
                        <p className="font-semibold text-slate-800" data-id="element-234">
                          ${rental.total.toFixed(2)}
                        </p>
                        {rental.hasInvoice && <span className="text-xs text-slate-500 mt-1 block" data-id="element-235">
                            Invoice exists
                          </span>}
                      </div>
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Invoice Modal */}
      {editingInvoice && <InvoiceEditor invoice={editingInvoice} onSave={updateInvoice} onClose={() => setEditingInvoice(null)} onDelete={() => {
      deleteInvoice(editingInvoice.id);
      setEditingInvoice(null);
    }} data-id="element-236" />}
    </div>;
}
function InvoiceEditor({
  invoice,
  onSave,
  onClose,
  onDelete
}: {
  invoice: Invoice;
  onSave: (invoice: Invoice) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [editedInvoice, setEditedInvoice] = useState<Invoice>({
    ...invoice
  });
  const addLineItem = () => {
    const newItem: LineItem = {
      id: Math.random().toString(),
      description: '',
      quantity: 1,
      price: 0,
      total: 0
    };
    setEditedInvoice({
      ...editedInvoice,
      lineItems: [...editedInvoice.lineItems, newItem]
    });
  };
  const updateLineItem = (id: string, field: keyof LineItem, value: any) => {
    const updated = editedInvoice.lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'price') {
          updatedItem.total = updatedItem.quantity * updatedItem.price;
        }
        return updatedItem;
      }
      return item;
    });
    setEditedInvoice({
      ...editedInvoice,
      lineItems: updated
    });
  };
  const removeLineItem = (id: string) => {
    setEditedInvoice({
      ...editedInvoice,
      lineItems: editedInvoice.lineItems.filter(item => item.id !== id)
    });
  };
  const handleSave = () => {
    onSave(editedInvoice);
  };
  const subtotal = editedInvoice.lineItems.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - editedInvoice.discount + editedInvoice.tax;
  const balance = total - editedInvoice.amountPaid;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4" data-id="element-237">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-id="element-238">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10" data-id="element-239">
          <div data-id="element-240">
            <h3 className="text-xl font-bold text-slate-800" data-id="element-241">Edit Invoice</h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-242">
              {editedInvoice.invoiceNumber}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-243">
            <X size={20} data-id="element-244" />
          </button>
        </div>

        <div className="p-6 space-y-6" data-id="element-245">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4" data-id="element-246">
            <div data-id="element-247">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-248">
                Customer Name
              </label>
              <input type="text" value={editedInvoice.customerName} onChange={e => setEditedInvoice({
              ...editedInvoice,
              customerName: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-249" />
            </div>
            <div data-id="element-250">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-251">
                Customer Email
              </label>
              <input type="email" value={editedInvoice.customerEmail} onChange={e => setEditedInvoice({
              ...editedInvoice,
              customerEmail: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-252" />
            </div>
          </div>

          {/* Line Items */}
          <div data-id="element-253">
            <div className="flex items-center justify-between mb-3" data-id="element-254">
              <label className="block text-sm font-medium text-slate-700" data-id="element-255">
                Line Items
              </label>
              <button onClick={addLineItem} className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm" data-id="element-256">
                <Plus size={14} data-id="element-257" />
                Add Item
              </button>
            </div>
            <div className="space-y-2" data-id="element-258">
              {editedInvoice.lineItems.map(item => <div key={item.id} className="flex gap-2 items-start" data-id="element-259">
                  <input type="text" placeholder="Description" value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-260" />
                  <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)} className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-261" />
                  <input type="number" placeholder="Price" value={item.price} onChange={e => updateLineItem(item.id, 'price', parseFloat(e.target.value) || 0)} className="w-28 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-262" />
                  <div className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-right font-medium" data-id="element-263">
                    ${item.total.toFixed(2)}
                  </div>
                  <button onClick={() => removeLineItem(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-264">
                    <Trash2 size={16} data-id="element-265" />
                  </button>
                </div>)}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-slate-200 pt-4 space-y-3" data-id="element-266">
            <div className="flex justify-between items-center" data-id="element-267">
              <span className="text-slate-700" data-id="element-268">Subtotal</span>
              <span className="font-semibold text-slate-800" data-id="element-269">
                ${subtotal.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between items-center" data-id="element-270">
              <label className="text-slate-700" data-id="element-271">Discount</label>
              <div className="flex items-center gap-2" data-id="element-272">
                <span className="text-slate-500" data-id="element-273">$</span>
                <input type="number" value={editedInvoice.discount} onChange={e => setEditedInvoice({
                ...editedInvoice,
                discount: parseFloat(e.target.value) || 0
              })} className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right" data-id="element-274" />
              </div>
            </div>

            <div className="flex justify-between items-center" data-id="element-275">
              <label className="text-slate-700" data-id="element-276">Tax</label>
              <div className="flex items-center gap-2" data-id="element-277">
                <span className="text-slate-500" data-id="element-278">$</span>
                <input type="number" value={editedInvoice.tax} onChange={e => setEditedInvoice({
                ...editedInvoice,
                tax: parseFloat(e.target.value) || 0
              })} className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right" data-id="element-279" />
              </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-280">
              <span className="font-semibold text-slate-800" data-id="element-281">Total</span>
              <span className="text-2xl font-bold text-slate-900" data-id="element-282">
                ${total.toFixed(2)}
              </span>
            </div>

            {editedInvoice.amountPaid > 0 && <>
                <div className="flex justify-between items-center text-green-600" data-id="element-283">
                  <span data-id="element-284">Amount Paid</span>
                  <span className="font-semibold" data-id="element-285">
                    ${editedInvoice.amountPaid.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-red-600" data-id="element-286">
                  <span className="font-semibold" data-id="element-287">Balance Due</span>
                  <span className="text-xl font-bold" data-id="element-288">
                    ${balance.toFixed(2)}
                  </span>
                </div>
              </>}
          </div>
        </div>

        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-6 flex justify-between" data-id="element-289">
          <button onClick={onDelete} className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-290">
            Delete Invoice
          </button>
          <div className="flex gap-3" data-id="element-291">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-292">
              Cancel
            </button>
            <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-293">
              Save Invoice
            </button>
          </div>
        </div>
      </div>
    </div>;
}